import clsx from 'clsx'
import { rem } from 'polished'
import * as React from 'react'
import styled from 'styled-components'

import { LANE_GAP, LANE_HEIGHT } from './CalendarDayCell'

const DAYS_IN_WEEK = 7
// Two 10px handles sit outside their bars, so this keeps a clear space
// between their circular outlines at a consecutive-stop boundary.
const SHARED_BOUNDARY_GAP_PX = 10

const Bar = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  height: ${rem(LANE_HEIGHT)};
  padding: 0 ${rem(6)};
  font-size: ${rem(12)};
  font-weight: 600;
  white-space: nowrap;
  overflow: visible;

  &.starts {
    border-start-start-radius: ${rem(4)};
    border-end-start-radius: ${rem(4)};
  }

  &.ends {
    border-start-end-radius: ${rem(4)};
    border-end-end-radius: ${rem(4)};
  }

  &.has-start-handle {
    padding-inline-start: ${rem(16)};
  }

  /*
   * Fade the cut edge so a bar continuing next week doesn't look truncated.
   *
   * The fade belongs on the inline edge the span runs past, but
   * linear-gradient() has no logical keywords -- an inline-end direction is
   * invalid syntax and drops the whole mask silently. So the physical
   * keywords are mirrored under [dir="rtl"] by hand, the same way
   * LuxKit/SvgIcon flips a directional icon. The lint rule cannot see this:
   * it maps physical properties, not gradient direction keywords.
   */
  &.fade-left {
    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 ${rem(9)});
    mask-image: linear-gradient(to right, transparent 0, #000 ${rem(9)});
  }

  [dir="rtl"] &.fade-left {
    -webkit-mask-image: linear-gradient(to left, transparent 0, #000 ${rem(9)});
    mask-image: linear-gradient(to left, transparent 0, #000 ${rem(9)});
  }

  &.fade-right {
    -webkit-mask-image: linear-gradient(to left, transparent 0, #000 ${rem(9)});
    mask-image: linear-gradient(to left, transparent 0, #000 ${rem(9)});
  }

  [dir="rtl"] &.fade-right {
    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 ${rem(9)});
    mask-image: linear-gradient(to right, transparent 0, #000 ${rem(9)});
  }

  &.fade-both {
    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 ${rem(9)}, #000 calc(100% - ${rem(9)}), transparent 100%);
    mask-image: linear-gradient(to right, transparent 0, #000 ${rem(9)}, #000 calc(100% - ${rem(9)}), transparent 100%);
  }
`

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
`

const Continuation = styled.span`
  opacity: 0.55;
  font-weight: 400;
`

interface Props {
  columnStart: number
  columnSpan: number
  isStart: boolean
  isEnd: boolean
  lane: number
  foreground?: string
  background?: string
  /** Hidden when the bar is too narrow to read, e.g. on mobile. */
  label?: React.ReactNode
  icon?: React.ReactNode
  /** Optional interactive controls at the real start and end of a span. */
  startHandle?: React.ReactNode
  endHandle?: React.ReactNode
  /** Start the first segment at the leading edge of its day cell. */
  startAtCellEdge?: boolean
  /** Optional inset from the leading edge of the first day cell. */
  startInsetPx?: number
  /** End the last segment at the trailing edge of its day cell. */
  endAtCellEdge?: boolean
  /** Optional inset from the trailing edge of the last day cell. */
  endInsetPx?: number
  /** Adds a small visual gutter at a shared itinerary boundary. */
  hasSharedStartBoundary?: boolean
  /** Adds a small visual gutter at a shared itinerary boundary. */
  hasSharedEndBoundary?: boolean
  className?: string
}

/**
 * One week's slice of a span.
 *
 * The bar runs from the middle of its first cell to the middle of its last,
 * matching how a stay actually works: you arrive partway through the first
 * day and leave partway through the last.
 */
function CalendarSpanBar(props: Props) {
  const {
    columnStart, columnSpan, isStart, isEnd, lane, foreground, background,
    label, icon, startHandle, endHandle, startAtCellEdge = false, startInsetPx = 0,
    endAtCellEdge = false, endInsetPx = 0,
    hasSharedStartBoundary = false, hasSharedEndBoundary = false, className,
  } = props

  const leftUnits = columnStart + (isStart && !startAtCellEdge ? 0.5 : 0)
  const rightUnits = columnStart + columnSpan - (isEnd && !endAtCellEdge ? 0.5 : 0)
  const startGap = isStart
    ? startInsetPx + (hasSharedStartBoundary ? SHARED_BOUNDARY_GAP_PX : 0)
    : 0
  const endGap = isEnd
    ? endInsetPx + (hasSharedEndBoundary ? SHARED_BOUNDARY_GAP_PX : 0)
    : 0

  const style = React.useMemo<React.CSSProperties>(() => ({
    insetInlineStart: `calc(${(leftUnits / DAYS_IN_WEEK) * 100}% + ${rem(startGap)})`,
    width: `calc(${((rightUnits - leftUnits) / DAYS_IN_WEEK) * 100}% - ${rem(startGap + endGap)})`,
    top: rem(lane * (LANE_HEIGHT + LANE_GAP)),
    background,
    color: foreground,
  }), [leftUnits, rightUnits, lane, background, foreground, startGap, endGap])

  const classes = clsx(className, {
    starts: isStart,
    ends: isEnd,
    'has-start-handle': Boolean(startHandle),
    'fade-both': !isStart && !isEnd,
    'fade-left': !isStart && isEnd,
    'fade-right': isStart && !isEnd,
  })

  return <Bar className={classes} style={style}>
    <Content>
      {icon}
      {label && (isStart ? label : <Continuation>{label}</Continuation>)}
    </Content>
    {startHandle}
    {endHandle}
  </Bar>
}

export default CalendarSpanBar
